import { effectiveRatio, mapNodes } from '@/core/document/tree'
import {
  CollageFamily,
  GradientPresetId,
  getCollageTemplate,
  getGradientPreset,
} from '@/content/collageTemplates'
import { outputRatioLabel, validateOutputSettings } from '@/core/document/output'
import { assertAdjustmentsParams, isIdentityAdjustments } from '@/core/document/adjustments'
import {
  DOCUMENT_VERSION,
  CollageBackgroundKind,
  MAX_COLLAGE_BACKGROUND_BLUR,
  MAX_COLLAGE_GAP,
  MAX_TREE_DEPTH,
  NodeKind,
  TransformKind,
  type CompositionNode,
  type Document,
  type SourceId,
} from '@/core/document/types'
import {
  assertIntegerRectInsideBounds,
  assertOrientationParams,
  assertRectMatchesRatio,
  isIdentityOrientation,
  orientedSize,
  rectInsideRotatedImage,
} from '@/core/geometry'
import type { Size } from '@/core/geometry'
import { assertHexColor, validateFrameParams } from '@/tools/frame/params'

export function validateUniqueNodeIds(nodes: Iterable<CompositionNode>): void {
  const seenIds = new Set<string>()
  for (const node of nodes) {
    if (!node.id.trim()) throw new Error('Document node id must not be empty')
    if (seenIds.has(node.id)) throw new Error(`Document contains duplicate node id "${node.id}"`)
    seenIds.add(node.id)
  }
}

export function validateDocument(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
): void {
  if (document.version !== DOCUMENT_VERSION) {
    throw new Error(`Unsupported document version: "${String(document.version)}"`)
  }
  validateOutputSettings(document.output)
  const nodes: CompositionNode[] = []
  mapNodes(document.root, (node) => {
    nodes.push(node)
    return node
  })
  validateUniqueNodeIds(nodes)

  function validateDepth(node: CompositionNode, depth: number): void {
    if (depth > MAX_TREE_DEPTH) throw new Error(`Document tree depth must not exceed ${MAX_TREE_DEPTH}`)
    if (node.kind === NodeKind.Frame) validateDepth(node.child, depth + 1)
    if (node.kind === NodeKind.Collage) {
      for (const child of [...node.cells, ...node.tray]) {
        if (child) validateDepth(child, depth + 1)
      }
    }
  }
  validateDepth(document.root, 1)

  for (const node of nodes) {
    switch (node.kind) {
      case NodeKind.Image: {
        const source = sources.get(node.sourceId)
        if (!source) {
          throw new Error(`Image node "${node.id}" references missing source "${node.sourceId}"`)
        }
        for (const transform of Object.keys(node.transforms)) {
          if (!Object.values(TransformKind).includes(transform as TransformKind)) {
            throw new Error(`Image node "${node.id}" has unknown transform "${transform}"`)
          }
        }
        const orientation = node.transforms[TransformKind.Orientation]
        if (orientation) {
          assertOrientationParams(orientation)
          if (isIdentityOrientation(orientation)) {
            throw new Error('Identity Orientation must be omitted from the transform stack')
          }
        }
        const crop = node.transforms[TransformKind.Crop]
        if (crop) {
          const bounds = orientedSize(source, orientation)
          assertIntegerRectInsideBounds(crop.rect, bounds, 'Crop rectangle')
          if (orientation?.straightenDeg
            && !rectInsideRotatedImage(crop.rect, bounds, orientation.straightenDeg)) {
            throw new Error('Crop rectangle must stay inside the straightened image')
          }
          if (node.id !== document.root.id) {
            assertRectMatchesRatio(
              crop.rect,
              effectiveRatio(document, sources, node.id),
              'Crop rectangle',
              outputRatioLabel(document.output),
            )
          }
        }
        const adjustments = node.transforms[TransformKind.Adjustments]
        if (adjustments) {
          assertAdjustmentsParams(adjustments)
          if (isIdentityAdjustments(adjustments)) {
            throw new Error('Identity Adjustments must be omitted from the transform stack')
          }
        }
        break
      }
      case NodeKind.Frame:
        validateFrameParams(node.params)
        break
      case NodeKind.Collage: {
        const template = getCollageTemplate(node.templateId)
        if (node.cells.length !== template.cells.length) {
          throw new Error(`Collage "${node.id}" requires ${template.cells.length} cells for template "${template.id}"`)
        }
        if (!Number.isFinite(node.gap) || node.gap < 0 || node.gap > MAX_COLLAGE_GAP) {
          throw new Error(`Collage gap must be from 0 to ${MAX_COLLAGE_GAP}`)
        }
        if (template.family === CollageFamily.Prints && node.gap !== 0) {
          throw new Error('Prints collage templates require a gap of 0')
        }
        switch (node.background.kind) {
          case CollageBackgroundKind.Color:
            assertHexColor(node.background.color, 'Collage background color')
            break
          case CollageBackgroundKind.Gradient:
            if (!Object.values(GradientPresetId).includes(node.background.presetId)) {
              throw new Error(`Unknown gradient preset: "${String(node.background.presetId)}"`)
            }
            getGradientPreset(node.background.presetId)
            assertHexColor(node.background.colors[0], 'Collage gradient start color')
            assertHexColor(node.background.colors[1], 'Collage gradient end color')
            break
          case CollageBackgroundKind.Image:
            if (!sources.has(node.background.sourceId)) {
              throw new Error(`Collage "${node.id}" background references missing source "${node.background.sourceId}"`)
            }
            if (!Number.isFinite(node.background.blur)
              || node.background.blur < 0
              || node.background.blur > MAX_COLLAGE_BACKGROUND_BLUR) {
              throw new Error(`Collage background blur must be from 0 to ${MAX_COLLAGE_BACKGROUND_BLUR} px`)
            }
            break
          default:
            throw new Error(`Collage "${node.id}" has unknown background kind "${String((node.background as { kind: unknown }).kind)}"`)
        }
        break
      }
      default:
        throw new Error(`Document contains unknown node kind "${String((node as { kind: unknown }).kind)}"`)
    }
  }
}
